import { useMemo } from 'react';
import dayjs from 'dayjs';
import { usePolledMetrics } from './useDynoMetrics';

// Poll interval for the slower changing status endpoints
const SLOW_POLL_INTERVAL_MS = 5 * 60 * 1000;

export type RollupStatus = 'ok' | 'warning' | 'critical';

export interface RqWorkerEntry {
    name: string;
    group: 'worker' | 'syncworker' | 'all' | 'other';
    queues: string[];
    state: string | null;
    seconds_since_heartbeat: number | null;
    healthy: boolean;
    current_job: { func: string; queue: string; started_at: string | null } | null;
}

export interface RqQueueEntry {
    name: string;
    depth: number;
    oldest_age_seconds: number | null;
    started_count: number | null;
    failed_count: number | null;
}

export interface RqStatusResult {
    checked_at: string | null;
    status: RollupStatus | null;
    error?: string;
    workers: RqWorkerEntry[];
    groups: Record<string, { covered: boolean; uncovered_queues: string[] }>;
    scheduler: { alive: boolean; scheduled_job_count: number };
    queues: RqQueueEntry[];
}

export interface BackupStatusResult {
    deployment: string | null;
    status: 'ok' | 'warning' | 'running' | 'failed' | 'missing' | null;
    latest: {
        status: string;
        started_at: string;
        finished_at: string | null;
        size_bytes: number | null;
        s3_key: string;
        error: string;
    } | null;
    previous_size_bytes: number | null;
    checked_at: string | null;
}

export interface LatestOrderEntry {
    id: number;
    environment_name: string;
    package: string;
    origin: string;
    duration: string;
    seats: number;
    module_names: string[];
    mrr: number;
    created: string;
    start_date: string;
}

export interface LatestTrialEntry {
    id: number;
    company_name: string;
    first_name: string;
    last_name: string;
    email: string;
    industry: string | null;
    company_size: string | null;
    trial_type: string | null;
    package: string | null;
    start_date: string;
    end_date: string;
    wizard_done: boolean;
    is_active: boolean;
    created: string;
}

export interface GrowthPoint {
    timestamp: string;
    environments: number;
    users: number;
}

// Empty params object kept stable for the polling hook
const NO_PARAMS: Record<string, string> = {};

// Polls the rq health endpoint
export function useRqStatus(viewerToken?: string) {
    const { result, loading } = usePolledMetrics<RqStatusResult>(
        'get_rq_status',
        NO_PARAMS,
        { checked_at: null, status: null, workers: [], groups: {}, scheduler: { alive: false, scheduled_job_count: 0 }, queues: [] },
        viewerToken
    );

    return { rqStatus: result, loading };
}

// Polls the backup status endpoint on a slow interval
export function useBackupStatus(viewerToken?: string) {
    const { result, loading } = usePolledMetrics<BackupStatusResult>(
        'get_backup_status',
        NO_PARAMS,
        { deployment: null, status: null, latest: null, previous_size_bytes: null, checked_at: null },
        viewerToken,
        SLOW_POLL_INTERVAL_MS
    );

    return { backupStatus: result, loading };
}

// Polls the total mrr endpoint and sums the per subscription values
export function useTotalMrr(viewerToken?: string) {
    // Keep the date param stable for the lifetime of the page
    const params = useMemo(() => ({ to: dayjs().format('YYYY-MM-DD') }), []);

    const { result, loading } = usePolledMetrics<{ id: number; value: number }[]>(
        'get_total_mrr',
        params,
        [],
        viewerToken,
        SLOW_POLL_INTERVAL_MS
    );

    // Sum the mrr values of every subscription
    const totalMrr = Array.isArray(result) ? result.reduce((sum, entry) => sum + (entry.value || 0), 0) : 0;

    return { totalMrr, loading };
}

// Polls the latest orders endpoint on a slow interval
export function useLatestOrders(viewerToken?: string) {
    const { result, loading } = usePolledMetrics<{ orders: LatestOrderEntry[] }>(
        'get_latest_orders',
        NO_PARAMS,
        { orders: [] },
        viewerToken,
        SLOW_POLL_INTERVAL_MS
    );

    return { orders: result.orders, loading };
}

// Polls the latest trials endpoint on a slow interval
export function useLatestTrials(viewerToken?: string) {
    const { result, loading } = usePolledMetrics<{ trials: LatestTrialEntry[] }>(
        'get_latest_trials',
        NO_PARAMS,
        { trials: [] },
        viewerToken,
        SLOW_POLL_INTERVAL_MS
    );

    return { trials: result.trials, loading };
}

// Polls the growth metrics endpoint over the last ninety days
export function useGrowthMetrics(viewerToken?: string) {
    // Keep the range params stable for the lifetime of the page
    const params = useMemo(() => ({
        from: dayjs().subtract(90, 'day').toISOString(),
        to: dayjs().toISOString(),
    }), []);

    const { result, loading } = usePolledMetrics<{ points: GrowthPoint[] }>(
        'get_growth_metrics',
        params,
        { points: [] },
        viewerToken,
        SLOW_POLL_INTERVAL_MS
    );

    return { points: result.points, loading };
}
